// src/pages/public/SenalesGratis.jsx
// Landing pública de captación Meta Ads → grupo free de Telegram.
// Pixel-perfect del mockup en design_handoff_telegram_funnel/.
//
// Sin auth ni ProtectedRoute. Mobile-first (ads de Instagram en celular).
// Carga rápida: estilos inline scopeados, sin dependencias pesadas.
//
// Meta Pixel:
//   - PageView al mount (necesario para SPA porque index.html ya disparó uno
//     al cargar la app, pero esta ruta puede activarse después y queremos
//     que cuente como pageview de conversión separada)
//   - Lead + trackCustom 'JoinTelegramFree' al click del CTA
//
// Countdown: replica del JS original del mockup. 30 min por visitante,
// persiste en localStorage (key 'ee_funnel_deadline_v1') para mantenerlo
// entre recargas. Cuando llega a 00:00:00 se renueva automáticamente.

import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { trackPageView, trackJoinTelegramFree } from '../../utils/metaPixel';

// ============================================================================
// CSS — traducción 1:1 del <style> del mockup. Scopeado a .ee-funnel-root
// para no contaminar otras páginas (que pueden usar .page, .brand, etc).
// ============================================================================
const FUNNEL_CSS = `
.ee-funnel-root {
  --accent: #FF4D0D;
  --accent-2: #FF6B2C;
  --accent-3: #FF8C42;
  --dark: #F5F4F2;
  --dark-2: #FFFFFF;
  --ink: #0E0E10;
  --muted: #6B6B72;
  --line: rgba(10,10,12,.1);
  --green: #16A34A;
  --font-display: "Inter Tight",-apple-system,BlinkMacSystemFont,"Helvetica Neue",system-ui,sans-serif;
  --font-sans: "Inter",-apple-system,BlinkMacSystemFont,"Helvetica Neue",system-ui,sans-serif;
  --font-mono: "JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace;

  background: var(--dark);
  color: var(--ink);
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 100svh;
  overflow-x: hidden;
  position: relative;
}
.ee-funnel-root * { box-sizing: border-box; }
.ee-funnel-root a { color: inherit; }

.ee-funnel-root .bg-glow {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background:
    radial-gradient(90% 55% at 50% -12%, color-mix(in oklab, var(--accent) 16%, transparent) 0%, transparent 62%),
    radial-gradient(70% 45% at 50% 112%, color-mix(in oklab, var(--accent-3) 10%, transparent) 0%, transparent 58%);
}
.ee-funnel-root .bg-dots {
  position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: .6;
  background-image: radial-gradient(rgba(10,10,12,.07) 1px, transparent 1px);
  background-size: 26px 26px;
}

.ee-funnel-root .page {
  position: relative; z-index: 1;
  max-width: 480px; margin: 0 auto;
  padding: 28px 22px calc(34px + env(safe-area-inset-bottom));
  display: flex; flex-direction: column; align-items: center; gap: 0;
  text-align: center;
}

.ee-funnel-root .brand { display: inline-flex; align-items: baseline; white-space: nowrap; }
.ee-funnel-root .brand .nm { font-family: var(--font-display); font-weight: 700; font-size: 22px; letter-spacing: -.04em; line-height: 1; color: var(--ink); }
.ee-funnel-root .brand .ball { width: 8px; height: 8px; margin-left: 3px; align-self: flex-end; flex: none; }

.ee-funnel-root .live-pill {
  /* Replica EXACTA del mockup standalone. Forzamos los valores con
     !important porque hay CSS globales del dashboard (mockup-mobile.css,
     index.css) que pisan font-size / letter-spacing / font-weight en
     selectores .pill genéricos y hacen que se vea más grande/separada
     que el HTML original. */
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  margin-top: 22px !important;
  padding: 7px 14px !important;
  border-radius: 999px !important;
  background: #fff !important;
  border: 1px solid var(--line) !important;
  font-family: var(--font-sans) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
  color: var(--ink) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  white-space: nowrap !important;
  box-shadow: 0 2px 8px -4px rgba(10,10,12,.12) !important;
}
.ee-funnel-root .live-pill * { text-transform: none !important; letter-spacing: 0 !important; }
.ee-funnel-root .live-pill .free-b {
  color: #0E7A3C !important;
  font-weight: 800 !important;
  letter-spacing: .04em !important;
}
.ee-funnel-root .live-dot {
  width: 8px; height: 8px; border-radius: 50%; background: var(--green);
  box-shadow: 0 0 0 0 rgba(34,197,94,.6);
  animation: ee-pulse-g 1.8s infinite;
}
@keyframes ee-pulse-g {
  0% { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  70% { box-shadow: 0 0 0 9px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}

.ee-funnel-root h1 {
  font-family: var(--font-display); font-weight: 900;
  font-size: clamp(34px, 9.4vw, 44px); line-height: .98; letter-spacing: -.035em;
  margin: 18px 0 0; text-wrap: balance;
}
.ee-funnel-root h1 .accent { color: var(--accent-2); }
.ee-funnel-root .sub { font-size: 15.5px; line-height: 1.5; color: var(--muted); margin: 14px 0 0; max-width: 340px; }
.ee-funnel-root .sub strong { color: var(--ink); }

.ee-funnel-root .photo-wrap { position: relative; margin-top: 28px; width: 190px; height: 190px; }
.ee-funnel-root .photo-ring {
  position: absolute; inset: -7px; border-radius: 50%;
  background: conic-gradient(from 140deg, var(--accent), var(--accent-3), transparent 62%, transparent 78%, var(--accent));
  animation: ee-spin 7s linear infinite;
}
@media (prefers-reduced-motion: reduce) { .ee-funnel-root .photo-ring { animation: none; } }
@keyframes ee-spin { to { transform: rotate(360deg); } }
.ee-funnel-root .photo-hole { position: absolute; inset: -2px; border-radius: 50%; background: var(--dark); }
.ee-funnel-root .photo-img { position: relative; display: block; width: 190px; height: 190px; border-radius: 50%; object-fit: cover; background: #000; }
.ee-funnel-root .free-tag {
  position: absolute; left: -14px; top: 6px; z-index: 2;
  transform: rotate(-12deg);
  background: var(--green); color: #fff;
  font-family: var(--font-display); font-weight: 900; font-size: 14px; letter-spacing: .1em;
  padding: 6px 13px; border-radius: 999px;
  box-shadow: 0 10px 24px -8px rgba(22,163,74,.6);
  border: 2px solid #fff;
}
.ee-funnel-root .photo-badge {
  position: absolute; right: 2px; bottom: 6px; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--accent); border: 3px solid var(--dark);
  box-shadow: 0 8px 20px -6px color-mix(in oklab, var(--accent) 70%, transparent);
}
.ee-funnel-root .photo-badge svg { width: 21px; height: 21px; fill: #fff; transform: translateX(-1px); }

.ee-funnel-root .cta {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  width: 100%; margin-top: 30px; padding: 19px 24px; border-radius: 16px;
  border: none; cursor: pointer; text-decoration: none;
  background: linear-gradient(180deg, var(--accent-2), var(--accent)); color: #fff;
  font-family: var(--font-display); font-weight: 800; font-size: 18px; letter-spacing: -.01em; line-height: 1.15;
  box-shadow: 0 16px 38px -12px color-mix(in oklab, var(--accent) 75%, transparent), inset 0 1px 0 rgba(255,255,255,.25);
  transition: transform .14s ease, box-shadow .14s ease;
  animation: ee-breathe 2.6s ease-in-out infinite;
}
.ee-funnel-root .cta:active { transform: scale(.97); }
.ee-funnel-root .cta svg { width: 24px; height: 24px; fill: #fff; flex: none; }
@keyframes ee-breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.025); } }
@media (prefers-reduced-motion: reduce) { .ee-funnel-root .cta { animation: none; } }
.ee-funnel-root .cta-note { margin-top: 12px; font-size: 13px; color: rgba(255,255,255,.5); }
.ee-funnel-root .cta-note b { color: rgba(255,255,255,.8); }

.ee-funnel-root .count-label {
  margin-top: 34px;
  font-family: var(--font-mono); font-size: 11px; font-weight: 700; letter-spacing: .18em;
  text-transform: uppercase; color: #B5430C;
}
.ee-funnel-root .count { display: flex; gap: 10px; margin-top: 12px; }
.ee-funnel-root .count .cell { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.ee-funnel-root .count .v {
  font-family: var(--font-mono); font-weight: 700; font-size: 34px; letter-spacing: -.02em; line-height: 1;
  min-width: 74px; padding: 16px 10px; border-radius: 14px;
  background: var(--dark-2); border: 1px solid var(--line);
  font-variant-numeric: tabular-nums;
  box-shadow: 0 4px 14px -8px rgba(10,10,12,.2);
  color: var(--ink);
}
.ee-funnel-root .count .l { font-size: 11px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: var(--muted); }
.ee-funnel-root .count .sep { font-family: var(--font-mono); font-weight: 700; font-size: 30px; color: rgba(10,10,12,.35); align-self: flex-start; padding-top: 18px; }

.ee-funnel-root .benefits { display: flex; flex-direction: column; gap: 11px; margin-top: 34px; width: 100%; max-width: 340px; text-align: left; }
.ee-funnel-root .benefit { display: flex; align-items: flex-start; gap: 12px; font-size: 14.5px; line-height: 1.45; color: rgba(14,14,16,.78); }
.ee-funnel-root .benefit .ck {
  flex: none; display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  background: rgba(22,163,74,.12); color: #0E7A3C;
  font-size: 12px; font-weight: 800; margin-top: 1px;
}
.ee-funnel-root .benefit strong { color: var(--ink); }

.ee-funnel-root .dl-q { margin-top: 36px; font-size: 14px; color: var(--muted); }
.ee-funnel-root .dl-row { display: flex; gap: 12px; margin-top: 14px; justify-content: center; flex-wrap: wrap; }
.ee-funnel-root .store-badge {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  /* width fija para que Google Play y App Store queden EXACTAMENTE del
     mismo ancho. Sin esto cada badge se ajusta a su contenido (texto
     "Google Play" es más largo que "App Store") y quedan distintos. */
  width: 170px;
  height: 54px; padding: 0 14px; border-radius: 9px;
  text-decoration: none; color: #fff;
  background: #000; border: 1px solid #A6A6A6;
  font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", "Segoe UI", Roboto, sans-serif;
  transition: transform .14s ease;
}
.ee-funnel-root .store-badge:active { transform: scale(.97); }
.ee-funnel-root .store-ic { width: 27px; height: 27px; flex: none; }
.ee-funnel-root .store-ic.apple { width: 30px; height: 30px; margin-top: -4px; }
.ee-funnel-root .store-txt { display: flex; flex-direction: column; align-items: flex-start; gap: 1px; line-height: 1.05; text-align: left; white-space: nowrap; }
.ee-funnel-root .store-txt small { font-size: 10px; font-weight: 400; letter-spacing: .02em; color: #fff; }
.ee-funnel-root .store-txt small.caps { font-size: 9px; letter-spacing: .08em; }
.ee-funnel-root .store-txt b { font-weight: 600; font-size: 20px; letter-spacing: -.02em; }
.ee-funnel-root .dl-q strong { color: var(--ink); }

@media (min-width: 768px) {
  .ee-funnel-root .page { max-width: 560px; padding-top: 48px; }
  .ee-funnel-root h1 { font-size: 54px; }
  .ee-funnel-root .sub { font-size: 17px; max-width: 420px; }
  .ee-funnel-root .photo-wrap { width: 220px; height: 220px; margin-top: 34px; }
  .ee-funnel-root .photo-wrap .photo-img { width: 220px; height: 220px; }
  .ee-funnel-root .cta { max-width: 420px; font-size: 19px; }
  .ee-funnel-root .count .v { font-size: 38px; min-width: 84px; }
  .ee-funnel-root .benefits { max-width: 420px; }
  .ee-funnel-root .dl-row { gap: 14px; }
}

.ee-funnel-root .foot {
  margin-top: 38px; padding-top: 18px; border-top: 1px solid var(--line);
  width: 100%; font-size: 11.5px; line-height: 1.5; color: var(--muted);
}
`;

// ============================================================================
// Countdown evergreen — replica del JS original del mockup.
// 30 min por visitante, persiste en localStorage. Cuando llega a 0 se renueva.
// ============================================================================
const COUNT_KEY = 'ee_funnel_deadline_v1';
const DURATION_MIN = 30;

function loadDeadline() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return Date.now() + DURATION_MIN * 60000;
  }
  const raw = parseInt(window.localStorage.getItem(COUNT_KEY) || '0', 10);
  if (!raw || raw < Date.now()) {
    const fresh = Date.now() + DURATION_MIN * 60000;
    try { window.localStorage.setItem(COUNT_KEY, String(fresh)); } catch (e) { /* private mode / quota */ }
    return fresh;
  }
  return raw;
}

function renewDeadline() {
  const fresh = Date.now() + DURATION_MIN * 60000;
  if (typeof window !== 'undefined' && window.localStorage) {
    try { window.localStorage.setItem(COUNT_KEY, String(fresh)); } catch (e) { /* ignore */ }
  }
  return fresh;
}

function pad(n) { return n < 10 ? '0' + n : '' + n; }

function formatCountdown(leftMs) {
  const t = Math.max(0, Math.floor(leftMs / 1000));
  return {
    h: pad(Math.floor(t / 3600)),
    m: pad(Math.floor((t % 3600) / 60)),
    s: pad(t % 60),
  };
}

// ============================================================================
// Componente
// ============================================================================
export default function SenalesGratis() {
  const deadlineRef = useRef(loadDeadline());
  const [time, setTime] = useState(() => formatCountdown(deadlineRef.current - Date.now()));

  // Pixel: PageView al mount.
  useEffect(() => {
    trackPageView();
  }, []);

  // Countdown loop — mismo intervalo 250ms del mockup original.
  useEffect(() => {
    let alive = true;
    function tick() {
      if (!alive) return;
      let left = deadlineRef.current - Date.now();
      if (left <= 0) {
        deadlineRef.current = renewDeadline();
        left = deadlineRef.current - Date.now();
      }
      setTime(formatCountdown(left));
    }
    tick();
    const id = setInterval(tick, 250);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Click CTA Telegram → dispara evento Lead + custom JoinTelegramFree.
  // No previene el navigate — el <a target="_blank"> abre Telegram igual.
  function handleCtaClick() {
    try { trackJoinTelegramFree(); } catch (e) { /* never break the CTA */ }
  }

  return (
    <>
      <Helmet>
        <title>Señales gratis - Edge Elite</title>
        <meta name="description" content="Pronósticos en vivo. Únete gratis al canal de Telegram." />
        <meta property="og:title" content="Señales gratis - Edge Elite" />
        <meta property="og:description" content="Pronósticos en vivo. Únete gratis al canal de Telegram." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://edgeelite.cl/senales-gratis" />
        <meta property="og:image" content="https://edgeelite.cl/assets/edgeelite_emblema_640.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Señales gratis - Edge Elite" />
        <meta name="twitter:description" content="Pronósticos en vivo. Únete gratis al canal de Telegram." />
        <meta name="twitter:image" content="https://edgeelite.cl/assets/edgeelite_emblema_640.png" />
        {/* Fuentes usadas por la landing (no las carga el index.html base) */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@700;800;900&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@600;700&display=swap" rel="stylesheet" />
        <style type="text/css">{FUNNEL_CSS}</style>
      </Helmet>

      <div className="ee-funnel-root">
        <div className="bg-glow"></div>
        <div className="bg-dots"></div>

        <main className="page" data-screen-label="Funnel Telegram">
          <div className="brand">
            <span className="nm">Edge Elite</span>
            <svg className="ball" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="12" fill="#FF4D0D"></circle>
              <path d="M6 6c3.2 2.6 3.2 9.8 0 12" stroke="#0A0A0C" strokeOpacity="0.55" strokeWidth="1.6" strokeLinecap="round" fill="none"></path>
            </svg>
          </div>

          <span className="live-pill">
            <span className="live-dot"></span> Pronósticos activos hoy · <b className="free-b">100% GRATIS</b>
          </span>

          <h1 id="headline">
            Los mejores pronósticos de hoy<br />te esperan <span className="accent">aquí</span> ↓
          </h1>

          <p className="sub">
            Análisis con <strong>ventaja estadística real</strong>, directo a tu Telegram.
          </p>

          <div className="photo-wrap">
            <div className="photo-ring"></div>
            <div className="photo-hole"></div>
            <img className="photo-img" src="/assets/edgeelite_emblema_640.png" alt="Edge Elite" />
            <span className="free-tag">GRATIS</span>
            <span className="photo-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M21.9 4.1c.3-1.2-.9-2.2-2-1.7L2.7 9.3c-1.2.5-1.1 2.2.1 2.6l4.6 1.5 1.8 5.6c.3 1 1.6 1.3 2.3.5l2.5-2.6 4.8 3.5c.9.7 2.2.2 2.4-.9L21.9 4.1zM9.4 13.1l8.7-5.5c.2-.1.4.1.2.3l-7 6.7-.3 3-1.6-4.5z" />
              </svg>
            </span>
          </div>

          <a
            className="cta"
            id="cta-btn"
            href="https://t.me/+x9EWlem4OtZmZWNh"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleCtaClick}
          >
            <svg viewBox="0 0 24 24">
              <path d="M21.9 4.1c.3-1.2-.9-2.2-2-1.7L2.7 9.3c-1.2.5-1.1 2.2.1 2.6l4.6 1.5 1.8 5.6c.3 1 1.6 1.3 2.3.5l2.5-2.6 4.8 3.5c.9.7 2.2.2 2.4-.9L21.9 4.1zM9.4 13.1l8.7-5.5c.2-.1.4.1.2.3l-7 6.7-.3 3-1.6-4.5z" />
            </svg>
            <span id="cta-text">¡ÚNETE GRATIS AL CANAL!</span>
          </a>

          <div className="count-label">El acceso gratis cierra en</div>
          <div className="count" aria-live="off">
            <div className="cell"><span className="v" id="c-h">{time.h}</span><span className="l">Horas</span></div>
            <span className="sep">:</span>
            <div className="cell"><span className="v" id="c-m">{time.m}</span><span className="l">Min</span></div>
            <span className="sep">:</span>
            <div className="cell"><span className="v" id="c-s">{time.s}</span><span className="l">Seg</span></div>
          </div>

          <div className="benefits">
            <div className="benefit"><span className="ck">✓</span><span><strong>Análisis de básquet</strong> sobre 94 ligas, con cuota y mercado exacto — y otros deportes.</span></div>
            <div className="benefit"><span className="ck">✓</span><span><strong>Nivel de confianza y edge</strong> en cada señal. Tú decides si entrar.</span></div>
            <div className="benefit"><span className="ck">✓</span><span><strong>Te llega solo</strong> — sin abrir apps. Directo al Telegram.</span></div>
          </div>

          <p className="dl-q">¿Aún no tienes Telegram? <strong>Bájalo gratis en 10 segundos</strong> y entra:</p>
          <div className="dl-row">
            <a
              className="store-badge"
              href="https://play.google.com/store/apps/details?id=org.telegram.messenger"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Descargar Telegram en Google Play"
            >
              <svg className="store-ic" viewBox="0 0 32 32" aria-hidden="true">
                <defs>
                  <linearGradient id="gp-a" x1="12" y1="3" x2="3" y2="28" gradientUnits="userSpaceOnUse"><stop stopColor="#00C3FF" /><stop offset="1" stopColor="#1BE2FA" /></linearGradient>
                  <linearGradient id="gp-b" x1="5" y1="3" x2="24" y2="12" gradientUnits="userSpaceOnUse"><stop stopColor="#2BD865" /><stop offset="1" stopColor="#00F076" /></linearGradient>
                  <linearGradient id="gp-c" x1="24" y1="21" x2="6" y2="30" gradientUnits="userSpaceOnUse"><stop stopColor="#FF3A44" /><stop offset="1" stopColor="#C31162" /></linearGradient>
                  <linearGradient id="gp-d" x1="21" y1="11.5" x2="28" y2="16" gradientUnits="userSpaceOnUse"><stop stopColor="#FFDD00" /><stop offset="1" stopColor="#FBBC04" /></linearGradient>
                </defs>
                <path d="M5.5 3.6c-.5.3-.8.8-.8 1.5v21.8c0 .7.3 1.2.8 1.5L17.9 16 5.5 3.6z" fill="url(#gp-a)" />
                <path d="M22.1 11.8 7.3 3.5c-.6-.3-1.3-.4-1.8-.1L17.9 16l4.2-4.2z" fill="url(#gp-b)" />
                <path d="M22.1 20.2 17.9 16 5.5 28.4c.5.3 1.2.3 1.8-.1l14.8-8.1z" fill="url(#gp-c)" />
                <path d="M26.5 14.2l-4.4-2.4-4.2 4.2 4.2 4.2 4.4-2.4c1.4-.8 1.4-2.8 0-3.6z" fill="url(#gp-d)" />
              </svg>
              <span className="store-txt"><small className="caps">DISPONIBLE EN</small><b>Google Play</b></span>
            </a>
            <a
              className="store-badge"
              href="https://apps.apple.com/app/telegram-messenger/id686449807"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Descargar Telegram en App Store"
            >
              <svg className="store-ic apple" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M16.4 12.9c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8-.7 0-1.8-.8-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7 2-1.1 2.8-2.2c.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.5-1-2.5-3.8zM14.1 5.9c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.7 1.4-.6.7-1.2 1.9-1 2.9 1 .1 2.1-.5 2.7-1.3z" fill="#fff" />
              </svg>
              <span className="store-txt"><small>Consíguelo en el</small><b>App Store</b></span>
            </a>
          </div>

          <footer className="foot">
            Edge Elite es una herramienta de análisis estadístico. No garantiza resultados.<br />
            Juega con responsabilidad · Solo +18.
          </footer>
        </main>
      </div>
    </>
  );
}
